// --- Dependencies -------------------------------------------------------------------------------
import { useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyFile, TyFileCategory } from "@/types";
import { formatBytes, rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import type { TyIconName } from "@/UI/atoms/Icon/types";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
const icons: Record<TyFileCategory, TyIconName> = { image: "image", video: "video", document: "document", model: "model", archive: "archive" };

type Props = { file: TyFile; onOpen: () => void; onActions: (anchor: DOMRect) => void };

/**
 * One file: the same width as a folder, and taller. Deliberately a different shape — a file explorer
 * separates folders from files so a glance tells you which half of the screen you are looking at.
 *
 * The thumbnail fades in once it has actually decoded, over the category icon. It is hidden with
 * `opacity`, never `display: none` — a lazily-loaded image that is not displayed is never in the
 * viewport, so the browser never fetches it, `onLoad` never fires and it stays hidden forever.
 *
 * Whatever fails — a thumbnail that was never generated, a bucket policy not yet applied, a signed
 * URL that expired while the page sat open — the icon underneath is the honest answer.
 */
export default function FileCard({ file, onOpen, onActions }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [thumb, setThumb] = useState<"loading" | "ready" | "failed">(file.thumb.url ? "loading" : "failed");

    return (
        <article className="file-card" onContextMenu={rightClickToOpen(onActions)}>
            <button type="button" className="file-card__preview" onClick={onOpen} title={file.name}>
                {file.thumb.url && (
                    <img
                        className={`file-card__thumb${thumb === "ready" ? " file-card__thumb--ready" : ""}`}
                        src={file.thumb.url}
                        alt=""
                        loading="lazy"
                        onLoad={() => setThumb("ready")}
                        onError={() => setThumb("failed")}
                    />
                )}
                {thumb !== "ready" && <Icon name={icons[file.category]} size={30} />}
            </button>

            <div className="file-card__row">
                <button type="button" className="file-card__identity" onClick={onOpen} title={file.name}>
                    <span className="file-card__name">{file.name}</span>
                    <span className="file-card__meta">
                        {t.drive.categories[file.category]} · {formatBytes(file.size, lang)}
                        {file.tags.length > 0 && ` · ${file.tags.join("، ")}`}
                    </span>
                </button>

                <button
                    ref={buttonRef}
                    type="button"
                    className="file-card__actions"
                    onClick={() => {
                        const rect = buttonRef.current?.getBoundingClientRect();
                        if (rect) onActions(rect);
                    }}
                    aria-label={t.common.more}
                >
                    <Icon name="more" size={20} />
                </button>
            </div>
        </article>
    );
}
